import React, { useEffect, useMemo } from 'react';
import { Box, Typography, Button, Fab, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import ProjectCard from 'src/components/ProjectCard';
import { useHistory } from 'react-router-dom';
import { ROUTES_URL } from 'src/constants/routes';
import DesktopLayout from 'src/layout/DesktopLayout';
import useProjectsStore from 'src/stores/projects';
import useUserStore from 'src/stores/user';
import { TProject, IProjectResponse } from 'src/types/Projects';
import { ProjectWithSubscription } from 'src/types/Subscription';
import ProjectsService from 'src/services/ProjectsService';
import Title from 'src/components/Title';

const ProjectsGrid = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
}));

const StyledFab = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1000,
}));

const ProjectsScreen: React.FC = () => {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { projects, setProjects, updateProject, createProject, setActiveProject, deleteProject } = useProjectsStore();
    const { current_subscription } = useUserStore(({ current_subscription }) => ({ current_subscription }));

    // Загружаем проекты при монтировании компонента и при возврате на страницу
    useEffect(() => {
        const loadProjects = async () => {
            try {
                const projectsData = await ProjectsService.getProjects();
                setProjects(projectsData);
            } catch (error) {
                console.error('Ошибка загрузки проектов:', error);
            }
        };

        loadProjects();
    }, [setProjects]);

    // Обновляем проекты при возврате на страницу (например, после создания нового проекта)
    useEffect(() => {
        const handleFocus = () => {
            const loadProjects = async () => {
                try {
                    const projectsData = await ProjectsService.getProjects();
                    setProjects(projectsData);
                } catch (error) {
                    console.error('Ошибка загрузки проектов:', error);
                }
            };
            loadProjects();
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [setProjects]);

    const handleProjectClick = (project: IProjectResponse | ProjectWithSubscription) => {
        // Здесь можно добавить логику открытия конкретного проекта
        console.log('Opening project:', project);
        // Пока что просто показываем алерт
        alert(`Открываем проект: ${project.name}`);
    };

    const handleToggleActive = async (projectId: number, isActive: boolean) => {
        try {
            // Если пытаемся активировать проект, проверяем лимит
            if (isActive) {
                const projectsLimit = current_subscription?.plan.projects_limit || 0;
                const activeProjectsCount = projects.filter((p) => p.is_active).length;

                // Если проект уже был активен, не считаем его дважды
                const currentProject = projects.find((p) => p.id === projectId);
                const wasActive = currentProject?.is_active || false;

                // Проверяем, есть ли активный тариф
                if (!current_subscription) {
                    enqueueSnackbar('Для активации проекта необходимо выбрать тариф', { variant: 'warning' });
                    return;
                }

                if (!wasActive && activeProjectsCount >= projectsLimit) {
                    enqueueSnackbar(
                        `Достигнут лимит активных проектов (${projectsLimit}). Сначала деактивируйте один из активных проектов.`,
                        { variant: 'warning' }
                    );
                    return;
                }
            }

            await updateProject({ id: projectId, is_active: isActive });
            enqueueSnackbar(isActive ? 'Проект активирован' : 'Проект деактивирован', { variant: 'success' });
        } catch (error) {
            console.error('Ошибка обновления проекта:', error);
            enqueueSnackbar('Ошибка обновления проекта', { variant: 'error' });
        }
    };

    // Проверяем, можно ли создать новый проект
    const canCreateProject = useMemo(() => {
        // Если тариф не выбран, разрешаем создать только 1 проект
        if (!current_subscription) {
            return projects.length < 1;
        }

        const projectsLimit = current_subscription.plan.projects_limit || 0;
        // Если лимит = 0, значит без ограничений
        if (projectsLimit === 0) return true;
        return projects.length < projectsLimit;
    }, [projects.length, current_subscription]);

    const projectsLimit = current_subscription?.plan.projects_limit || 0;

    const handleCreateProject = async () => {
        try {
            // Проверяем лимит перед созданием
            if (!canCreateProject) {
                if (!current_subscription) {
                    enqueueSnackbar(
                        'Для создания более одного проекта необходимо выбрать тариф. Удалите существующий проект или выберите тариф.',
                        { variant: 'warning' }
                    );
                } else {
                    enqueueSnackbar(
                        `Достигнут лимит проектов (${projectsLimit}). Удалите один из проектов или обновите тариф для увеличения лимита.`,
                        { variant: 'warning' }
                    );
                }
                return;
            }

            // Создаем проект с дефолтным именем на бэкенде
            const newProject = await createProject({
                name: 'Новый проект',
                project_goal: 'attract_clients',
                audience_description: '',
            });
            // Устанавливаем созданный проект как активный
            setActiveProject(newProject);
            // Перезагружаем проекты для обновления списка
            const projectsData = await ProjectsService.getProjects();
            setProjects(projectsData);
            // Переходим на страницу заполнения
            history.push(ROUTES_URL.NEW_PROJECT);
        } catch (error) {
            console.error('Ошибка создания проекта:', error);
            enqueueSnackbar('Ошибка создания проекта', { variant: 'error' });
        }
    };

    const handleDeleteProject = async (projectId: number) => {
        try {
            console.log('handleDeleteProject вызван для проекта:', projectId);
            await deleteProject(projectId);
            console.log('deleteProject из store выполнен успешно');
            // Перезагружаем проекты для обновления списка
            const projectsData = await ProjectsService.getProjects();
            console.log('Проекты перезагружены:', projectsData.length);
            setProjects(projectsData);
            enqueueSnackbar('Проект успешно удален', { variant: 'success' });
        } catch (error: any) {
            console.error('Ошибка удаления проекта:', error);
            const errorMessage = error?.response?.data?.error || error?.message || 'Ошибка удаления проекта';
            enqueueSnackbar(errorMessage, { variant: 'error' });
            throw error; // Пробрасываем ошибку, чтобы модальное окно могло обработать её
        }
    };

    const renderContent = () => (
        <Box
            pt={2}
            minHeight="100%"
            display="flex"
            flexDirection="column"
        >
            <Box mb={1.5}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <Box>
                        <Title>Проекты</Title>
                    </Box>
                    {canCreateProject ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreateProject}
                        >
                            Добавить проект
                        </Button>
                    ) : (
                        <Tooltip
                            title={
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 0.5 }}
                                    >
                                        {!current_subscription
                                            ? 'Достигнут лимит проектов без тарифа (1 проект)'
                                            : `Достигнут лимит проектов (${projectsLimit})`}
                                    </Typography>
                                    <Typography variant="body2">
                                        {!current_subscription
                                            ? 'Для создания более одного проекта необходимо выбрать тариф. Удалите существующий проект или выберите тариф.'
                                            : 'Удалите один из проектов или обновите тариф для увеличения лимита'}
                                    </Typography>
                                </Box>
                            }
                            arrow
                            placement="top"
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCreateProject}
                                    disabled={!canCreateProject}
                                >
                                    Добавить проект
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            </Box>
            <Box
                maxWidth="80%"
                mb={20 / 8}
            >
                <Typography
                    variant="body1"
                    color="primary"
                >
                    Управляйте своими проектами и отслеживайте их статус
                </Typography>
            </Box>

            {projects.length === 0 ? (
                <Box
                    textAlign="center"
                    py={8}
                    sx={{
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: 'grey.300',
                    }}
                >
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                    >
                        У вас пока нет проектов
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        Создайте свой первый проект для начала работы
                    </Typography>
                    {canCreateProject ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreateProject}
                        >
                            Добавить проект
                        </Button>
                    ) : (
                        <Tooltip
                            title={
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 0.5 }}
                                    >
                                        {!current_subscription
                                            ? 'Достигнут лимит проектов без тарифа (1 проект)'
                                            : `Достигнут лимит проектов (${projectsLimit})`}
                                    </Typography>
                                    <Typography variant="body2">
                                        {!current_subscription
                                            ? 'Для создания более одного проекта необходимо выбрать тариф. Удалите существующий проект или выберите тариф.'
                                            : 'Удалите один из проектов или обновите тариф для увеличения лимита'}
                                    </Typography>
                                </Box>
                            }
                            arrow
                            placement="top"
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCreateProject}
                                    disabled={!canCreateProject}
                                >
                                    Добавить проект
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            ) : (
                <ProjectsGrid>
                    {projects.map((project) => {
                        const activeProjectsCount = projects.filter((p) => p.is_active).length;
                        const projectsLimit = current_subscription?.plan.projects_limit || 0;

                        return (
                            <ProjectCard
                                key={project.id}
                                project={project}
                                onClick={handleProjectClick}
                                onToggleActive={handleToggleActive}
                                onDelete={handleDeleteProject}
                                activeProjectsCount={activeProjectsCount}
                                projectsLimit={projectsLimit}
                                hasActiveSubscription={!!current_subscription}
                            />
                        );
                    })}
                </ProjectsGrid>
            )}

            {/* Быстрое создание проекта отключено */}
        </Box>
    );

    return <DesktopLayout>{renderContent()}</DesktopLayout>;
};

export default ProjectsScreen;
